import React, { useEffect, useState } from "react";
import { tasksApi, TaskCodeSettings as TaskCodeSettingsT } from "../../api/tasks";
import { useToast } from "../../hooks/useToast";

export default function TaskCodeSettings() {
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<TaskCodeSettingsT | null>(null);

  // Local form state — mirrors `settings` while editing.
  const [prefix, setPrefix] = useState("TASK-");
  const [suffix, setSuffix] = useState("");
  // Padding is fixed at 1 — the field is no longer exposed in the UI,
  // so generated codes look like "TASK-1", "TASK-2" instead of zero-
  // padded "TASK-0001". Kept as a constant rather than a state to make
  // the preview / save plumbing one-line change.
  const padding = 1;
  const [startNumber, setStartNumber] = useState<number>(1);
  const [startNumberInput, setStartNumberInput] = useState<string>("1");

  // Initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await tasksApi.getCodeSettings();
        const s: TaskCodeSettingsT | null = r.data?.settings || null;
        if (cancelled) return;
        if (s) {
          setSettings(s);
          setPrefix(s.prefix ?? "TASK-");
          setSuffix(s.suffix ?? "");
          // Padding intentionally ignored — UI no longer exposes it.
          // Save will always send 1, normalising any existing setting.
          const initial = s.start_number ?? 1;
          setStartNumber(initial);
          setStartNumberInput(String(initial));
        }
      } catch (e: any) {
        toast({
          variant: "destructive",
          description: e?.response?.data?.detail || "Failed to load task code settings",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live preview using the *current* form values, not the server values.
  // We match the backend's f"{prefix}{n:0{width}d}{suffix}" format.
  const previewNumber = settings && startNumber === settings.start_number
    ? settings.next_number
    : startNumber;
  const previewPadded = String(previewNumber).padStart(Math.max(1, padding), "0");
  const preview = `${prefix}${previewPadded}${suffix}`;

  const handleSave = async () => {
    if (startNumber < 1) {
      toast({ variant: "destructive", description: "Start number must be at least 1." });
      return;
    }
    setSaving(true);
    try {
      const r = await tasksApi.saveCodeSettings({
        prefix,
        suffix,
        padding,
        start_number: startNumber,
      });
      const s: TaskCodeSettingsT = r.data?.settings;
      if (s) setSettings(s);
      toast({ description: "Task code settings saved." });
    } catch (e: any) {
      toast({
        variant: "destructive",
        description: e?.response?.data?.detail || "Failed to save task code settings",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading task code settings…</div>;
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Task ID Settings</h2>
        <p className="text-sm text-gray-500 mt-1">
          Set the series used to auto-generate the readable ID for every new task in this branch
          (e.g. <span className="font-mono">TASK-1</span>). The series is read-only on the
          Create Task form — only the values below change it.
        </p>
      </div>

      {/* Live preview pill */}
      <div className="mb-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
        <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">
          Next Task ID
        </span>
        <span className="font-mono text-lg font-bold text-indigo-700 tracking-wider">
          {preview}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Prefix</label>
          <input
            type="text"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            maxLength={20}
            placeholder="TASK-"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
          />
          <p className="text-xs text-gray-400 mt-1">Letters before the number, e.g. <span className="font-mono">TASK-</span>.</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Suffix</label>
          <input
            type="text"
            value={suffix}
            onChange={(e) => setSuffix(e.target.value)}
            maxLength={20}
            placeholder="(optional)"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
          />
          <p className="text-xs text-gray-400 mt-1">Optional, e.g. <span className="font-mono">-2026</span>.</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Start Number</label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={startNumberInput}
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, "");
              const cleaned = digits.replace(/^0+(?=\d)/, "");
              setStartNumberInput(cleaned);
              setStartNumber(cleaned === "" ? 0 : parseInt(cleaned, 10));
            }}
            onFocus={(e) => e.target.select()}
            onBlur={() => {
              // If the user left the field empty, snap back to a valid
              // string so the input doesn't look broken on re-render.
              if (startNumberInput === "") setStartNumberInput("0");
            }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-400 mt-1">
            The first number in the series. Saving resets the live counter to this value —
            existing tasks keep their codes.
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      {settings && (
        <p className="text-xs text-gray-400 mt-4">
          Counter currently at <span className="font-mono">{settings.next_number}</span>.
          {settings.updated_at && ` Last updated ${new Date(settings.updated_at).toLocaleString()}.`}
        </p>
      )}
    </div>
  );
}
